/**
 * KOSIMA (2026-10-07): when the shopper switches colour on the PDP (or in quick buy), show the FIRST image of the new
 * colour in the media order, not the variant's featured image. Image order is the only thing that arranges the PDP
 * gallery; snippets/product-gallery.liquid does the same for the first load.
 *
 * Prestige's own handler (theme.js, ProductGallery onVariantChange) listens on the product form and either selects the
 * variant's image (carousel layouts) or smooth-scrolls the PAGE to it (stacked desktop layout). Two smooth scrolls in a
 * row are not reliably resolved in favour of the second (Safari keeps the first running), so for the stacked layout
 * the theme's scroll is suppressed instead of out-raced:
 *
 *   1. capture phase on document (runs BEFORE the form's own listeners): give the one element the theme is about to
 *      scroll to a no-op scrollIntoView of its own;
 *   2. bubble phase on document (runs AFTER them): remove that override and scroll to the colour's first image.
 *
 * By then the gallery is already filtered to the new colour (product:rerender is dispatched just before
 * variant:change), so the first visible cell IS the colour's first image. Same trigger as the theme: only when the
 * featured media changed, so a size change never moves the gallery. theme.js is untouched.
 */
(() => {
  const galleriesFor = (event) => {
    const { variant, previousVariant } = event.detail ?? {};
    if (!variant?.featured_media || previousVariant?.featured_media?.id === variant.featured_media.id) return [];

    // getAttribute, not form.id: <input name="id"> shadows the property (see product-title-color.js).
    const form = event.target;
    const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
    if (!formId) return [];

    return [...document.querySelectorAll(`product-gallery[form="${CSS.escape(formId)}"]`)].filter((gallery) => gallery.carousel);
  };

  const suppressed = new Set();

  document.addEventListener('variant:change', (event) => {
    for (const gallery of galleriesFor(event)) {
      if (gallery.carousel.isScrollable) continue;

      const target = gallery.querySelector(`[data-media-id="${event.detail.variant.featured_media.id}"]`);
      if (target) {
        target.scrollIntoView = () => {};
        suppressed.add(target);
      }
    }
  }, true);

  document.addEventListener('variant:change', (event) => {
    suppressed.forEach((element) => delete element.scrollIntoView);
    suppressed.clear();

    for (const gallery of galleriesFor(event)) {
      if (gallery.carousel.isScrollable) {
        gallery.carousel.select(0, { instant: true });
      } else {
        gallery.querySelector('.product-gallery__media:not([hidden])')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  });
})();
